import { Result } from 'core/infrastructure';
import { PaymentDTO, PaymentMethodDTO, SaleDTO } from 'core/api';
import { CustomerDisplay, ExternalPaymentsService } from 'pos/services';
import { CreatePaymentScreen } from './CreatePaymentScreen';
import { CashdrawerFacade } from 'pos/facades';
import * as PaymentDTOFactory from './PaymentDTOFactory';
import { Modal } from 'core/ui-next/modal';

type Context = {
	mode: 'ADD' | 'REMOVE',
	ticketGlobalId: Nullable<string>,
	process: (payments: PaymentDTO[], amountToProcess: number) => Promise<Result<SaleDTO>>
};

type CreatePaymentContext = Context & {
	pendingAmount: number
	pendingPaymentParts?: { currentPart: number, totalParts: number }
}

type AddPaymentContext = Context & {
	method: PaymentMethodDTO,
	amountToPay: number;
	paidAmount: number,
	tipAmount: number,
}

function isCreatePaymentContext(context: CreatePaymentContext | AddPaymentContext): context is CreatePaymentContext {
	return (context as any).pendingAmount !== undefined;
}

export default async function processPayment(context: CreatePaymentContext | AddPaymentContext): Promise<Result> {

	const { mode, ticketGlobalId, process } = context;

	const operation = await CashdrawerFacade.createPaymentOperation();

	const paymentDataResult = isCreatePaymentContext(context)
		? await Modal.show(CreatePaymentScreen,
			{
				mode: mode,
				pendingAmount: context.pendingAmount,
				paymentOperation: operation,
				pendingPaymentParts: context.pendingPaymentParts
			})
		: Result.ok({ Method: context.method, AmountToPay: context.amountToPay, PaidAmount: context.paidAmount, TipAmount: context.tipAmount });

	if (!paymentDataResult.ok)
		return Result.error();

	const payment = PaymentDTOFactory.create(paymentDataResult.value.Method, paymentDataResult.value.PaidAmount, paymentDataResult.value.TipAmount);
	const amountToPay = paymentDataResult.value.AmountToPay;

	// El ExternalPayments trabaja siempre con pagos en positivo, el se encarga de invertirlos si hace falta en base al mode, por
	// tanto el payment que  nos devuelve CreatePaymentScreen hay que dejarlo en positivo siempre
	const externalPayments = mode === 'ADD' ? ExternalPaymentsService.forPay('PrePay', ticketGlobalId) : ExternalPaymentsService.forRefund('PreRefund', ticketGlobalId);
	const result = await externalPayments.register(payment);
	if (!result.ok)
		return Result.error();

	const change = payment.IsRefundVoucher ? 0 : payment.Amount - amountToPay;
	const cashPaymentResult = await externalPayments.commitCashPayments(change);
	if (!cashPaymentResult.ok)
		return Result.error();

	// En el process hay que enviar el signo de la operación porque luego el AddPaymentsHandlers del servidor espera
	// que esté en el signo adecuado para tratarlo como un pago o una devolución
	const conversionFactor = mode === 'ADD' ? 1 : -1;
	const newPayment: PaymentDTO = {
		...payment,
		Amount: payment.Amount * conversionFactor,
		TipAmount: payment.TipAmount * conversionFactor
	};
	const saleResult = await process([newPayment], amountToPay * conversionFactor);
	if (!saleResult.ok) {
		await externalPayments.undoAll();
		return Result.error();
	}

	await CustomerDisplay.displaySale(saleResult.value);

	return Result.ok();
}
